import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Select, Spin } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  isWithinRange,
  getAcademicMonths,
  getAcademicYears,
} from "../../Utils/academicPeriod";
import { fetchAcademicYear } from "../../Store/Slices/Common/AcademicYear/academicYear.action";

const { Option } = Select;

const AcademicPeriodPicker = ({ mode = "day", value, onChange }) => {
  const dispatch = useDispatch();

  // selector
  const { academicYears, loading } = useSelector(
    (state) => state.common.academicYear
  );

  // active academic year
  const academicYear = useMemo(() => {
    return academicYears?.find((y) => y.isActive) || null;
  }, [academicYears]);


  const [selectedDate, setSelectedDate] = useState(null);

  /* ================= FETCH ACADEMIC YEAR ================= */
  useEffect(() => {
    if (!academicYears?.length) {
      dispatch(fetchAcademicYear());
    }
  }, [dispatch, academicYears?.length]);

  const start = academicYear ? dayjs(academicYear.startDate) : null;
  const end = academicYear ? dayjs(academicYear.endDate) : null;
  const today = dayjs();

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    if (!academicYear) return;

    const defaultDate = isWithinRange(today, start, end)
      ? today
      : start;

    setSelectedDate(defaultDate);
  }, [academicYear]);

  /* ================= PRE-COMPUTED VALUES ================= */
  const months = useMemo(() => {
    if (!academicYear) return [];
    return getAcademicMonths(start, end);
  }, [academicYear]);

  const years = useMemo(() => {
    if (!academicYear) return [];
    return getAcademicYears(start, end);
  }, [academicYear]);

  /* ================= LOADING STATE ================= */
  if (loading || !academicYear || !selectedDate) {
    return <Spin />;
  }

  /* ================= DAY MODE ================= */
  if (mode === "day") {
    return (
      <Calendar
        fullscreen={false}
        value={selectedDate}
        disabledDate={(current) =>
          !isWithinRange(current, start, end) ||
          current.isAfter(today, "day")
        }

        /* 🔑 CUSTOM HEADER */
        headerRender={({ value, onChange }) => {
          const currentYear = value.year();
          const currentMonth = value.month() + 1;

          return (
            <div className="flex items-center gap-2 mb-2">
              {/* YEAR SELECT (academic only) */}
              <Select
                size="small"
                value={currentYear}
                onChange={(year) => {
                  const firstMonthOfYear = months.find(m => m.year === year);
                  if (!firstMonthOfYear) return;

                  onChange(
                    value
                      .year(year)
                      .month(firstMonthOfYear.month - 1)
                  );
                }}
              >
                {years.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>


              {/* MONTH SELECT (academic only for selected year) */}
              <Select
                size="small"
                value={currentMonth}
                onChange={(month) => {
                  onChange(value.month(month - 1));
                }}
              >
                {months
                  .filter((m) => m.year === currentYear)
                  .map((m) => (
                    <Option key={m.month} value={m.month}>
                      {m.label}
                    </Option>
                  ))}
              </Select>
            </div>
          );
        }}

        dateFullCellRender={(current) => {
          const disabled =
            !isWithinRange(current, start, end) ||
            current.isAfter(today, "day");

          const isSelected = current.isSame(selectedDate, "day");

          return (
            <div
              className={`
              w-8 h-8 flex items-center justify-center rounded
              ${disabled ? "bg-gray-200 text-gray-400" : "cursor-pointer"}
              ${isSelected ? "bg-green-500 text-white" : ""}
            `}
            >
              {current.date()}
            </div>
          );
        }}

        onSelect={(date) => {
          setSelectedDate(date);
          onChange?.(date.format("YYYY-MM-DD"));
        }}
      />
    );
  }


  /* ================= MONTH MODE ================= */
  if (mode === "month") {
    const selectValue =
      value?.month && value?.year
        ? `${value.month}-${value.year}`
        : `${start.month() + 1}-${start.year()}`;

    return (
      <Select
        className="w-full"
        value={selectValue}
        onChange={(val) => {
          const [month, year] = val.split("-");
          onChange?.({ month: Number(month), year: Number(year) });
        }}
      >
        {months.map((m) => (
          <Option key={`${m.month}-${m.year}`} value={`${m.month}-${m.year}`}>
            {m.label} {m.year}
          </Option>
        ))}
      </Select>
    );
  }

  /* ================= YEAR MODE ================= */
  if (mode === "year") {
    return (
      <Select
        className="w-full"
        value={selectedDate.year()}
        onChange={(year) => onChange?.({ year })}
      >
        {years.map((y) => (
          <Option key={y} value={y}>
            {y}
          </Option>
        ))}
      </Select>
    );
  }

  return null;
};

export default AcademicPeriodPicker;
